import { useEffect } from 'react';
import { useToasts } from '@/components/toaster';
import type { TwilioPhone } from './useTwilioPhone';

// =====================================================================
// The idle-external-mic toast: the concrete use of components/toaster.
//
// The icon pulse on the mic picker shipped first and did not move the number
// (see the cockpit-idle-external-mic monitor). An icon can only say "look at
// me"; this says what is wrong and switches the device in one click, which is
// the part the icon structurally could not do. The pulse stays — it is what
// points at the picker once the toast is dismissed.
//
// `show` is called from an effect on every relevant change and that is fine by
// design: the store replaces by id rather than stacking, and a dismissal sticks
// until `clear` (see lib/toasts.ts).
// =====================================================================

export const IDLE_MIC_TOAST_ID = 'cockpit.idle-external-mic';

export function useIdleMicToast(twilio: TwilioPhone): void {
  const { show, clear } = useToasts();
  const { idleExternalMic, status, inputDevices, selectInputDevice } = twilio;

  // Only while the device is live: with no registered Device the picker is
  // disabled, so offering to switch would hand them a button that does nothing.
  const connected = status === 'ready' || status === 'on_call';
  const active = connected && idleExternalMic !== null;

  useEffect(() => {
    if (!active || !idleExternalMic) {
      clear(IDLE_MIC_TOAST_ID);
      return;
    }
    // Resolve the label back to an id for the one-click switch. Looked up here
    // rather than carried on the snapshot because deviceIds are salted per
    // browser profile: valid to act on within this session, wrong to persist or
    // report (see audio-devices.ts).
    const target = inputDevices.find((d) => d.label === idleExternalMic);
    show({
      id: IDLE_MIC_TOAST_ID,
      tone: 'warn',
      title: "You're on the built-in microphone",
      body: `"${idleExternalMic}" is connected — prospects hear you better on it.`,
      // Without a resolvable id there is no one-click fix; the toast still says
      // what is wrong and the picker is a click away.
      ...(target
        ? { action: { label: 'Switch', onClick: () => selectInputDevice(target.deviceId) } }
        : {}),
    });
  }, [active, idleExternalMic, inputDevices, selectInputDevice, show, clear]);

  // Leaving the cockpit ends the episode: a stale toast must not outlive the
  // screen it refers to, and the next visit should be allowed to warn again.
  useEffect(() => () => clear(IDLE_MIC_TOAST_ID), [clear]);
}
